import type { FirefliesMeetingAttendee, FirefliesSentence, FirefliesTranscript } from '../types';

export const toActionItemsArray = (actionItems: unknown): string[] => {
  if (Array.isArray(actionItems)) return actionItems.filter((i) => typeof i === 'string');
  if (typeof actionItems === 'string') return [actionItems];
  return [];
};

export interface ParsedActionItem {
  person: string;
  text: string;
  timestamp?: string;
}

const TIMESTAMP_RE = /\s*\((\d{1,2}:\d{2}(?::\d{2})?)\)\s*/;
const PERSON_HEADER_RE = /\*\*(.+?)\*\*/g;

const TIMESTAMP_ONLY_RE = /^\d{1,2}:\d{2}(:\d{2})?$/;

export const parseActionItems = (raw: unknown): ParsedActionItem[] => {
  const strings = toActionItemsArray(raw);
  const results: ParsedActionItem[] = [];

  for (const str of strings) {
    const segments = str.split(PERSON_HEADER_RE);

    if (segments.length >= 3) {
      for (let i = 1; i < segments.length - 1; i += 2) {
        const person = segments[i].trim();
        const body = segments[i + 1];
        const parts = body.split(TIMESTAMP_RE);

        for (let j = 0; j < parts.length; j++) {
          const part = parts[j].trim();
          if (!part) continue;
          if (TIMESTAMP_ONLY_RE.test(part)) continue;

          const nextPart = parts[j + 1]?.trim();
          const hasTimestamp = nextPart && TIMESTAMP_ONLY_RE.test(nextPart);

          results.push({
            person,
            text: part,
            timestamp: hasTimestamp ? nextPart : undefined,
          });
          if (hasTimestamp) j++;
        }
      }
    } else {
      const cleaned = str.replace(/\*\*/g, '').trim();
      if (cleaned) {
        results.push({ person: '', text: cleaned });
      }
    }
  }

  return results;
};

export interface ActionItemGroup {
  person: string;
  isCurrentUser: boolean;
  items: { text: string; timestamp?: string }[];
}

export const getUserNames = (
  userEmail: string,
  attendees?: FirefliesMeetingAttendee[],
): Set<string> => {
  const names = new Set<string>();
  if (!userEmail) return names;
  const emailLower = userEmail.toLowerCase();
  names.add(emailLower);

  const localPart = emailLower.split('@')[0];
  if (localPart && localPart.length > 2) {
    const fromEmail = localPart.replace(/[._+]/g, ' ').trim();
    if (fromEmail.length > 2) names.add(fromEmail);
  }

  attendees?.forEach((a) => {
    if (a.email?.toLowerCase() === emailLower) {
      if (a.displayName) names.add(a.displayName.toLowerCase());
      if (a.name) names.add(a.name.toLowerCase());
    }
  });

  return names;
};

export const groupActionItems = (
  parsed: ParsedActionItem[],
  userNames: Set<string>,
): ActionItemGroup[] => {
  const groupMap = new Map<string, ActionItemGroup>();

  for (const item of parsed) {
    const key = item.person || '_ungrouped';
    if (!groupMap.has(key)) {
      const personLower = item.person.toLowerCase();
      const isCurrentUser = [...userNames].some(
        (name) => personLower.includes(name) || name.includes(personLower),
      );
      groupMap.set(key, { person: item.person, isCurrentUser, items: [] });
    }
    groupMap.get(key)!.items.push({ text: item.text, timestamp: item.timestamp });
  }

  const groups = [...groupMap.values()];
  groups.sort((a, b) => {
    if (a.isCurrentUser && !b.isCurrentUser) return -1;
    if (!a.isCurrentUser && b.isCurrentUser) return 1;
    return 0;
  });

  return groups;
};

export interface SummarySegment {
  type: 'text' | 'bold';
  value: string;
}

export interface SummaryBlock {
  type: 'paragraph' | 'bullet';
  segments: SummarySegment[];
}

export const parseSummaryText = (text: string): SummaryBlock[] => {
  const lines = text.split('\n');
  const blocks: SummaryBlock[] = [];

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    const isBullet = /^[-•*]\s+/.test(line);
    const content = isBullet ? line.replace(/^[-•*]\s+/, '') : line;
    const segments = parseInlineBold(content);

    blocks.push({ type: isBullet ? 'bullet' : 'paragraph', segments });
  }

  return blocks;
};

const parseInlineBold = (text: string): SummarySegment[] => {
  const segments: SummarySegment[] = [];
  const parts = text.split(/(\*\*.+?\*\*)/g);

  for (const part of parts) {
    if (!part) continue;
    if (part.startsWith('**') && part.endsWith('**')) {
      segments.push({ type: 'bold', value: part.slice(2, -2) });
    } else {
      segments.push({ type: 'text', value: part });
    }
  }

  return segments;
};

export const getMentionedSentences = (
  transcript: FirefliesTranscript,
  userEmail: string,
): FirefliesSentence[] => {
  const sentences = transcript.sentences ?? [];
  if (!userEmail) return [];

  const names = getUserNames(userEmail, transcript.meeting_attendees);

  return sentences.filter((s) => {
    const textLower = s.text.toLowerCase();
    const speakerLower = s.speaker_name?.toLowerCase() ?? '';
    for (const name of names) {
      if (name && (textLower.includes(name) || speakerLower.includes(name))) return true;
    }
    return false;
  });
};
